import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import { Checkbox } from '@mui/material';
import MDDateRange from 'components/MDDateRange';

const initFind = {
  search: '',
  order_by: 'rank',
  order_sort: 'asc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  categories: [],
  period: 'ALL',
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

function RpProfitRankingCat() {
  const mod = 'rp_profit_ranking_cat';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [cboCat, setCboCat] = useState([]);
  const [urlExcel, setUrlExcel] = useState('');

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'image', label: 'รูป', align: 'left', sort: false },
    { id: 'id', label: 'สินค้า', align: 'left' },
    { id: 'cat_name', label: 'หมวดสินค้า', align: 'left' },
    { id: 'rank', label: 'Rank', align: 'right' },
    { id: 'profit', label: '% กำไร', align: 'right' },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      cat: data.categories,
      period: data.period,
      date_start: data.date_start,
      date_stop: data.date_stop,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    const params = new URLSearchParams();
    params.append('search', find.search);
    find.categories.forEach((cat) => {
      params.append('cat[]', cat);
    });
    params.append('period', find.period);
    if (find.period !== 'ALL') {
      params.append('date_start', find.date_start);
      params.append('date_stop', find.date_stop);
    }

    const url = `${urlExcel}/download?${params.toString()}`;
    window.open(url, '_self');
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        result.categories.forEach((r) => {
          r.id = r.name;
        });
        result.categories = [{ id: null, name: 'ไม่มีหมวดหมู่' }, ...result.categories];
        setCboCat(result.categories);
        setUrlExcel(result.excel);

        const newFind = {
          ...find,
          categories: result.categories.map((b) => b.id),
          page: 1,
        };

        setFind(newFind);
        view(newFind);
      } else {
        Func.exitApp();
      }
    });
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              select
              fullWidth
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  if (selected.length === cboCat.length) return 'ทั้งหมด';
                  return cboCat
                    .filter((item) => selected.includes(item.id))
                    .map((item) => item.name)
                    .join(', ');
                },
              }}
              value={find.categories}
              onChange={(e) => {
                const { value } = e.target;

                if (value.includes('ALL')) {
                  const allSelected = find.categories.length === cboCat.length;
                  view({
                    ...find,
                    categories: allSelected ? [] : cboCat.map((item) => item.id),
                    page: 1,
                  });
                } else {
                  view({ ...find, categories: value, page: 1 });
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    {t('menu.products_level_category')} :
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>
                <Checkbox checked={find.categories.length === cboCat.length} />
                ทั้งหมด
              </MenuItem>
              {cboCat.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  <Checkbox checked={find.categories.indexOf(item.id) > -1} />
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          {find.period === 'ALL' ? (
            <Grid item xs={12} sm={12} md={6} lg={2}>
              <MDButton
                variant='outlined'
                color='secondary'
                fullWidth
                sx={{ p: 1.5 }}
                onClick={() => view({ ...find, period: 'DATE', page: 1 })}
              >
                <Icon>edit_calendar</Icon>&nbsp;เลือกช่วงเวลา
              </MDButton>
            </Grid>
          ) : null}
          {find.period === 'ALL' ? null : (
            <Grid item xs={12} sm={12} md={12} lg={5}>
              <MDDateRange
                dateStart={find.date_start}
                dateStop={find.date_stop}
                callBack={setDateRange}
                closeDate={() => view({ ...find, period: 'ALL', page: 1 })}
              />
            </Grid>
          )}
          {record.length !== 0 && (
            <Grid item xs={12} sm={12} md={12} lg={1}>
              <MDButton variant='gradient' color='success' fullWidth onClick={excel}>
                <Icon>table_view</Icon>&nbsp;Excel
              </MDButton>
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_power_bi'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={initHeader.length} align='center'>
                        ไม่พบข้อมูลกรุณาเลือกหมวดสินค้า
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{Func.imageDisplay(row)}</TableCell>
                      <TableCell align='left'>
                        <MDBox>{row.code}</MDBox>
                        <MDBox className='cut' width={200}>
                          {row.name}
                        </MDBox>
                      </TableCell>
                      <TableCell>{row.cat_name || '-'}</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.rank)}</TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(row.profit, 2)}</MDBox>
                        <MDBox>%</MDBox>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            {record.length === 0 ? null : (
              <Card sx={{ p: 2 }}>
                <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                  <MDButton
                    variant={show ? 'contained' : 'outlined'}
                    color='dark'
                    onClick={openShow}
                  >
                    {t(`element.show_record`, { num: find.perPage })}&nbsp;
                    <Icon>keyboard_arrow_down</Icon>
                  </MDButton>
                  {renderShow}
                  <Pagination
                    count={pageTotal}
                    variant='outlined'
                    color='secondary'
                    showFirstButton
                    showLastButton
                    page={pageNo}
                    onChange={(e, p) => view({ ...find, page: p })}
                  />
                </MDBox>
              </Card>
            )}
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_power_bi`), t(`menu.rp_profit_ranking_cat`)]} />
      {renderList()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpProfitRankingCat;
